import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { TiplymeLogo, GoogleButton } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      toast.success("Account created! Welcome to TiplyMe.");
      navigate("/app", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen md:grid-cols-2">
      <div className="hidden flex-col justify-between bg-brand-green p-10 text-white md:flex">
        <TiplymeLogo dark />
        <div>
          <h2 className="font-heading text-4xl font-extrabold leading-tight tracking-tight">Start earning tips today.</h2>
          <p className="mt-4 max-w-sm text-white/80">Create your worker wallet. A TiplyMe agent links your QR/NFC card and workplace.</p>
        </div>
        <p className="text-sm text-white/60">tiplyme.co.za</p>
      </div>

      <div className="flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="md:hidden"><TiplymeLogo /></div>
          <h1 className="mt-6 font-heading text-2xl font-bold">Create account</h1>
          <p className="mt-1 text-sm text-muted-foreground">Worker sign-up — free forever.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" required value={form.name} onChange={set("name")} data-testid="register-name-input" className="mt-1.5" placeholder="Thabo Mokoena" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="register-email-input" className="mt-1.5" placeholder="you@example.com" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="register-password-input" className="mt-1.5" placeholder="At least 6 characters" />
            </div>
            <Button type="submit" disabled={busy} data-testid="register-submit-button" className="w-full rounded-full bg-brand-green font-semibold text-white hover:bg-brand-green/90">
              {busy ? "Creating…" : "Create account"}
            </Button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" /> OR <span className="h-px flex-1 bg-border" />
          </div>
          <GoogleButton label="Sign up with Google" />

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-brand-green hover:underline" data-testid="go-login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
